import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { toolCallFailureLine, type ToolCallFailure } from './tool-call-failure';
import { conversationSpaceId, widgetToolCallBody } from './conversation-space';
// Client-side data layer for orion-web: the harness tool relay. Mirrors the fetch conventions of
// lib/tasks.ts: hit `${API_BASE}/api/...` with credentials. The API routes resolve the caller's metasite
// from their identity.
//
// Design tokens are no longer fetched here — the dashboard iframe renders via the dashboard service's
// `src` render URL, which serves a document that self-contains the metasite's design tokens + runtime
// (resolved server-side from the dashboard service, the single source of truth).

/** A managed surface's kind — an at-a-glance widget or a drill-in page. Shared with lib/widgets-folder. */
export type WidgetKind = 'widget' | 'page';

// Session-scoped cache for widget tool-call results — a reload of the same dashboard otherwise refetches
// every widget's data byte-identical (the render-doc + every /api/tool-call response have zero client
// caching today). Every call reaching this function is safe to cache: the harness's bridge is deny-by-
// default for a widget caller (`isBridgeAllowed` in orion-harness-poc/src/endpoints/tool-call.ts) — a
// widget can only reach read-only tools plus the three data-path execute tools (ExecuteWixAPI,
// ComposioExecuteTool, McpExecuteTool), and those three are gated the same way real writes are (the
// server-side denial is what actually keeps a widget from mutating, not anything client-side); there is
// no call this function can make that the server would have treated as a write. In-memory (module-level
// Map), not sessionStorage — this only needs to live as long as the tab does.
const MAX_CACHE_ENTRIES = 64;
interface CacheEntry {
  result: unknown;
}
const toolCallCache = new Map<string, CacheEntry>();
// Bumped by every clear, so a fetch that started before one never writes its older answer back.
let cacheGeneration = 0;

/** Sorted-key JSON so `{a:1,b:2}` and `{b:2,a:1}` hash identically. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`).join(',')}}`;
}

/** A Space's widgets never share a cached answer with another Space's (or the home's); no Space keeps the old key. */
function cacheKeyFor(tool: string, params: unknown, workspaceId?: string, workspaceSlug?: string): string {
  const key = `${tool}:${stableStringify(params ?? {})}`;
  const space = conversationSpaceId(workspaceId) ?? (workspaceSlug ? `slug:${workspaceSlug}` : undefined);
  return space ? `${space}|${key}` : key;
}

/** LRU touch: re-inserting a key moves it to the end of the Map's iteration order (most-recently-used). */
function touchCache(key: string, entry: CacheEntry): void {
  toolCallCache.delete(key);
  toolCallCache.set(key, entry);
  while (toolCallCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = toolCallCache.keys().next().value;
    if (oldestKey === undefined) break;
    toolCallCache.delete(oldestKey);
  }
}

/**
 * Host transport failures — the relay itself never answered, or answered as a broken gateway. These are
 * not the widget's bug and not the tool's; they are an outage between the browser and the harness. They
 * get one fixed shape, `[UPSTREAM UNAVAILABLE] <tool>: …`, which `dashboard-error-kind.ts` reads as an
 * (unnamed) `outage` instead of a defect. The same shape is emitted by the iOS and Android hosts.
 */
const UNAVAILABLE_STATUSES = new Set([502, 503, 504]);
const MAX_NETWORK_DETAIL = 120;
const RELAYED_UPSTREAM_CODES = new Set(['upstream_error', 'upstream_failed']);

/** A cancelled request (fetch AbortError, axios CanceledError) is not a failure and must stay silent. */
function isCancellation(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === 'AbortError' || name === 'CanceledError' || code === 'ERR_CANCELED';
}

/**
 * fetch rejects with a TypeError on a network failure — but so does a plain bug in our own code, so only
 * the browsers' network wordings count. The instance-auth http client says ERR_NETWORK, or a timeout code
 * (iOS `URLError.timedOut` and Android's `SocketTimeoutException` are outages too, so this keeps parity).
 */
const FETCH_NETWORK_WORDING = /\b(?:failed to fetch|load failed|networkerror|network request failed)\b/i;
const NETWORK_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT']);

function isNetworkFailure(error: unknown): boolean {
  if (error instanceof TypeError) return FETCH_NETWORK_WORDING.test(error.message);
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return (typeof code === 'string' && NETWORK_CODES.has(code)) || message === 'Network Error';
}

function networkDetail(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  const detail = raw.replace(/\s+/g, ' ').trim() || 'request failed';
  return detail.length > MAX_NETWORK_DETAIL ? detail.slice(0, MAX_NETWORK_DETAIL) : detail;
}

async function fetchToolResult(tool: string, params: unknown, workspaceId?: string, workspaceSlug?: string): Promise<unknown> {
  let res: Response;
  try {
    res = await apiFetch(`${API_BASE}/api/tool-call`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // The Space rides beside the tool when the dashboard is a Space's, which is what its reads resolve their site through.
      body: JSON.stringify(widgetToolCallBody(tool, params, workspaceId, workspaceSlug)),
    });
  } catch (error) {
    if (!isCancellation(error) && isNetworkFailure(error)) {
      throw new Error(`[UPSTREAM UNAVAILABLE] ${tool}: network error (${networkDetail(error)})`);
    }
    throw error;
  }
  const data = (await res.json().catch(() => ({}))) as { result?: unknown } & Partial<ToolCallFailure>;
  // `/api/tool-call` relays the harness's own gateway failures as `upstream_error` / `upstream_failed`
  // (or a bare gateway answers with no body). Any OTHER code with a gateway status — `503
  // no_bootstrap_context` — is our own misconfiguration, not an outage, and keeps today's defect path.
  if (UNAVAILABLE_STATUSES.has(res.status) && (!data.error || RELAYED_UPSTREAM_CODES.has(data.error))) {
    throw new Error(`[UPSTREAM UNAVAILABLE] ${tool}: service unavailable (HTTP ${res.status})`);
  }
  if (!res.ok) {
    throw new Error(toolCallFailureLine(tool, data, res.status));
  }
  return data.result;
}

/** Drops every cached result, so the next call of each fetches: after a card's write, the old row is wrong. */
export function clearWidgetToolResults(): void {
  toolCallCache.clear();
  cacheGeneration += 1;
}

/**
 * Relay one `orion.call(tool, params)` to the harness. Resolves with the tool's result.
 *
 * Stale-while-revalidate: a cache hit resolves IMMEDIATELY with the last-known value, and a fresh fetch
 * fires in the background (unawaited) to refresh the cache for next time — so a widget that already
 * rendered once elsewhere in the tab paints instantly instead of waiting on the network again. A cache
 * miss awaits the real call as before. Errors are never cached — a failed call always hits the network
 * again next time, so a transient failure can't wedge a widget in a broken state for the rest of the tab.
 */
export async function callWidgetTool(tool: string, params: unknown, workspaceId?: string, workspaceSlug?: string): Promise<unknown> {
  const key = cacheKeyFor(tool, params, workspaceId, workspaceSlug);
  const cached = toolCallCache.get(key);
  const generation = cacheGeneration;
  const store = (result: unknown) => {
    if (generation === cacheGeneration) touchCache(key, { result });
  };

  if (cached) {
    touchCache(key, cached);
    // Background refresh — deliberately not awaited/returned. A failed revalidation just leaves the
    // existing cache entry in place for the next read; it must never surface to THIS caller, which
    // already resolved off the cache.
    void fetchToolResult(tool, params, workspaceId, workspaceSlug)
      .then(store)
      .catch(() => {});
    return cached.result;
  }

  const result = await fetchToolResult(tool, params, workspaceId, workspaceSlug);
  store(result);
  return result;
}
